import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import AccessibilityIcon from './AccessibilityIcon'
import useReducedMotionPreference from '../../../hooks/useReducedMotionPreference'

const INTRO_DURATION = 1200
const CROSSFADE = 400
const SESSION_KEY = 'nico-a11y-intro-shown'

const Button = styled.button`
  position: fixed;
  top: 3rem;
  right: 1rem;
  z-index: 1000;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: 880px) {
    right: 3rem;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--tltr-awesome-value));
    outline-offset: 4px;
    border-radius: 2px;
  }

  &:hover .hb-bars span {
    opacity: 1;
  }

  &:hover .hb-bars span:last-child {
    width: 1.5rem;
  }

  @media (prefers-reduced-motion: reduce) {
    span,
    svg {
      transition: none !important;
    }
  }
`

const Center = styled.span`
  position: relative;
  display: inline-flex;
  width: 1.5rem;
  height: 1.5rem;
  align-items: center;
  justify-content: center;
`

// Default state: bars are visible, icon is hidden. When <html data-a11y-intro="pending">
// is set (by the SSR preload script on the first visit of a session), the
// attribute selector below flips visibility so the accessibility icon shows
// first. The effect in the component clears the attribute after INTRO_DURATION
// to trigger the crossfade to bars. This avoids any hydration mismatch because
// both SSR and client render identical markup; only the ambient <html>
// attribute drives initial visibility.
const IconWrapper = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--text-color));
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity ${CROSSFADE}ms ease,
    transform ${CROSSFADE}ms ease;
  pointer-events: none;

  :root[data-a11y-intro='pending'] & {
    opacity: 1;
    transform: scale(1);
  }
`

const BarsWrapper = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  opacity: 1;
  transform: scale(1);
  transition:
    opacity ${CROSSFADE}ms ease,
    transform ${CROSSFADE}ms ease;

  :root[data-a11y-intro='pending'] & {
    opacity: 0;
    transform: scale(0.6);
  }
`

const Bar = styled.span`
  display: block;
  width: 1.5rem;
  height: 1.5px;
  opacity: 0.5;
  background-color: rgb(var(--text-color));
  transition:
    transform 0.3s ease,
    opacity 0.15s ease,
    width 0.3s ease;
  transform-origin: center;
`

const TopBar = styled(Bar)`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: translateY(4px) rotate(45deg);
    `}
`

const BottomBar = styled(Bar)`
  width: 1rem;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      width: 1.5rem;
      transform: translateY(-4px) rotate(-45deg);
    `}
`

const clearIntro = () => {
  if (typeof document === 'undefined') return
  document.documentElement.removeAttribute('data-a11y-intro')
  try {
    sessionStorage.setItem(SESSION_KEY, '1')
  } catch {}
}

const HamburgerButton = React.forwardRef(({ isOpen, onClick }, ref) => {
  const reducedMotion = useReducedMotionPreference()
  const timerRef = useRef(null)

  // Drive the crossfade via the ambient <html data-a11y-intro> attribute.
  // On first mount of a session, the SSR script has set the attribute and
  // our CSS shows the icon. We schedule a timer to remove it and reveal
  // the bars. If reduced motion is on, we clear immediately.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const isPending =
      document.documentElement.getAttribute('data-a11y-intro') === 'pending'
    if (!isPending) return

    if (reducedMotion) {
      clearIntro()
      return
    }

    timerRef.current = setTimeout(() => {
      clearIntro()
      timerRef.current = null
    }, INTRO_DURATION)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [reducedMotion])

  // If the user opens the menu while the intro is still playing, end it
  // immediately so the burger → X transform is visible.
  useEffect(() => {
    if (!isOpen) return
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    clearIntro()
  }, [isOpen])

  return (
    <Button
      ref={ref}
      onClick={onClick}
      aria-expanded={isOpen}
      aria-controls="nav-overlay"
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
    >
      <Center>
        <IconWrapper aria-hidden="true">
          <AccessibilityIcon size={22} />
        </IconWrapper>
        <BarsWrapper className="hb-bars" aria-hidden="true">
          <TopBar $isOpen={isOpen} />
          <BottomBar $isOpen={isOpen} />
        </BarsWrapper>
      </Center>
    </Button>
  )
})

HamburgerButton.displayName = 'HamburgerButton'

export default HamburgerButton
